import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { FormControl } from '@material-ui/core';
import { ModalActionsComponent, PasslyTextField } from '@app/components';
import { getModalSwitch, getLoaderBtn, loadingData, handleReloadShellConfig } from '@app/store/actions';
import { IActiveFilter } from '@app/models';
import { useTypedTranslation } from '@app/hooks';
import { useDebounce, onPressEnterModal, genericErrorToast } from '@app/helpers';
import { organizationsService } from '@app/services';
import { AxiosResponse } from 'axios';

const validator = require('validator');

export default function AddOrganizationDialog(props: any) {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const [addOrgBtn, setAddOrgBtn] = useState(true);
    const [organizationNameValidation, setOrganizationNameValidation] = useState(false);
    const [organizationName, setOrganizationName] = useState('');
    const [homeRealm, setHomeRealm] = useState('');
    const [homeRealmValidation, setHomeRealmNameValidation] = useState(false);
    const [organizationNameTaken, setOrganizationNameTaken] = useState(false);
    const [orgs, setOrgs] = useState([]);
    const [filter, setFilter] = useState<IActiveFilter[]>([]);
    const [domain, setDomain] = useState<string>();
    const [errorMessage, setErrorMessage] = useState('');

    const debouncedOrganizationName = useDebounce(organizationName, 50);
    const debouncedHomeRealmName = useDebounce(homeRealm, 50);

    const regexCheckNameInput = new RegExp('^[A-Za-z0-9 _]*[A-Za-z0-9][A-Za-z0-9 _]*$');
    const regexCheckHomerealmInput = new RegExp('^[a-z0-9-]*$');

    let trimmedOrgName = organizationName.replace(/  +/g, ' ');
    let trimmedHomeRealm = homeRealm.replace(/ /g, '');

    useEffect(() => {
        handleGetOrganizationsShortDetailsData(filter);
    }, [debouncedOrganizationName, debouncedHomeRealmName]);

    useEffect(() => {
        if (orgs !== undefined && !orgs.length) {
            setOrganizationNameTaken(false);
        } else if (organizationName === '' && homeRealm === '') {
            setOrganizationNameTaken(false);
        } else {
            setOrganizationNameTaken(true);
        }
    }, [orgs]);

    useEffect(() => {
        if (
            organizationName.length &&
            homeRealm.length &&
            orgs !== undefined &&
            !orgs.length &&
            !organizationNameValidation &&
            !homeRealmValidation
        ) {
            setAddOrgBtn(false);
        } else {
            setAddOrgBtn(true);
        }
    }, [organizationName, homeRealm, orgs]);

    useEffect(() => {
        let currentDomain = window.location.hostname;

        // used only when running dev build using the command:
        // > npm start
        if (process.env.NODE_ENV === 'development') {
            if (process.env.REACT_APP_PASSLY_API_HOST) {
                currentDomain = new URL(process.env.REACT_APP_PASSLY_API_HOST).hostname;
            }
            else {
                throw 'Check REACT_APP_PASSLY_API_HOST is set in .env.development file';
            }
        }

        if (currentDomain) {
            setDomain('-' + currentDomain);
        } else {
            setDomain('-tenant.my.passly.com');
        }
    }, []);

    const handleSetValueFilter = (value: any) => {
        const filters: IActiveFilter[] = [
            {
                name: 'contains',
                value: `${value}${domain}`,
                label: 'HomeRealm',
                filterBy: 'HomeRealm'
            }
        ];
        setFilter(filters);
    };

    const handleGetOrganizationsShortDetailsData = (filters?: IActiveFilter[]) => {
        organizationsService
            .getOrganizationAvailable(filters)
            .then((response: AxiosResponse) => {
                setOrgs(response.data.value);
                dispatch(loadingData(false));
            })
            .catch(() => {
                dispatch(loadingData(false));
            });
    };

    const handleAddOrganization = (odataType: string, name: string, homeRealm: string, defaultIssuerUri: string) => {
        organizationsService
            .addOrganization(odataType, name, homeRealm, defaultIssuerUri)
            .then((response: AxiosResponse) => {
                dispatch<any>(handleReloadShellConfig());
                dispatch(loadingData(false));
            })
            .then(() => {
                dispatch(getModalSwitch(false));
                dispatch(getLoaderBtn(false));
            })
            .catch((error) => {
                if (error.response?.data.error.message === 'PAST_ORG_LIMIT') {
                    setErrorMessage(translation('PAST_ORG_LIMIT'));
                } else {
                    genericErrorToast(error);
                }

                dispatch(getLoaderBtn(false));
                dispatch(loadingData(false));
            });
    };

    const handleAddOrganizationName = (e: any) => {
        setOrganizationName(e.target.value);
        setHomeRealm(e.target.value.toLowerCase().replace(/ /g, ''));
        handleSetValueFilter(e.target.value);
        if (!validator.isEmpty(e.target.value) && regexCheckNameInput.test(e.target.value)) {
            setOrganizationNameValidation(false);
            setHomeRealmNameValidation(false);
        } else {
            setOrganizationNameValidation(true);
            setHomeRealmNameValidation(true);
        }
    };

    const handleAddHomeRealm = (e: any) => {
        setHomeRealm(e.target.value);
        if (!validator.isEmpty(e.target.value) && regexCheckHomerealmInput.test(e.target.value)) {
            setHomeRealmNameValidation(false);
        } else {
            setHomeRealmNameValidation(true);
        }
    };

    const handleCancel = () => {
        dispatch(getModalSwitch(false));
    };

    const handleSubmitBtn = () => {
        setAddOrgBtn(true);
        dispatch(getLoaderBtn(true));

        const fixedHomeRealm: string = calculateHomeRealm(trimmedHomeRealm, domain);

        handleAddOrganization(
            'Kaseya.AuthAnvil.Models.Organization',
            trimmedOrgName,
            fixedHomeRealm.toLocaleLowerCase(),
            `https://${trimmedHomeRealm}${domain}/trust`.toLocaleLowerCase()
        );
    };

    const calculateHomeRealm = (homerealm: string, tenantSuffix: string | undefined) => {
        const fullyQualified: string = homerealm + tenantSuffix;
        const firstDotIndex: number = fullyQualified.indexOf('.');

        if (fullyQualified && firstDotIndex > 0) {
            return fullyQualified.substring(0, firstDotIndex);
        }
        return fullyQualified;
    };

    return (
        <>
            <form
                className='modal__form modal__form--organization'
                method='post'
                tabIndex={0}
                onKeyPress={onPressEnterModal(handleSubmitBtn, addOrgBtn)}
            >
                <FormControl>
                    <PasslyTextField
                        label={'ORG_NAME'}
                        autoComplete={translation('ORG_NAME')}
                        value={organizationName}
                        onChange={handleAddOrganizationName}
                        id='displayName-input'
                        error={organizationNameValidation}
                        helperText={organizationNameValidation && translation('ORG_NAME_INVALID')}
                        required
                        autoFocus
                    />
                </FormControl>
                <FormControl>
                    <div className='modal__form--organization-realm'>
                        <div>
                        <PasslyTextField
                            label={'ORG_HOMEREALM'}
                            autoComplete={translation('USERNAME')}
                            value={homeRealm}
                            onChange={handleAddHomeRealm}
                            id='homeRealm-input'
                            required
                            error={homeRealmValidation || organizationNameTaken}
                            helperText={(homeRealmValidation && translation('ORG_HOMEREALM_INVALID')) || (organizationNameTaken && organizationName !== '' && homeRealm !== '' && translation('ORG_HOMEREALM_NOT_UNIQUE'))}
                        />
                        </div>
                        <p>{domain}</p>
                    </div>
                </FormControl>
                {errorMessage && <div className='modal__error'>{errorMessage}</div>}
            </form>
            <ModalActionsComponent
                primaryBtn={translation('ADD_ORG')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleSubmitBtn}
                onClickSecondary={handleCancel}
                disabled={addOrgBtn}
                id='addOrg'
            />
        </>
    );
}
